import React from "react";
import type { StockNewsItemData } from "@crypto-trading/common/browser";
import { DesktopSheet } from "@/layouts/DesktopSheet";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import type { LivePriceExchangeCode } from "@/hooks/useTokenLivePrice";

interface StockNewsDetailDrawerDesktopProps {
  item: StockNewsItemData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTrade?: () => void;
}

function formatPrice(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return "Not available";
  return `$${price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export const StockNewsDetailDrawerDesktop: React.FC<
  StockNewsDetailDrawerDesktopProps
> = ({ item, open, onOpenChange, onTrade }) => {
  const matched = Boolean(item?.primaryTicker);
  const liveExchange = (item?.alertPriceExchange ||
    "dynamic") as LivePriceExchangeCode;
  const { price: livePrice } = useTokenLivePrice(
    matched ? item?.priceSymbol : null,
    liveExchange,
    open && matched
  );

  return (
    <DesktopSheet
      open={open}
      onOpenChange={onOpenChange}
      title={item?.primaryTicker || "Stock news"}
      width={480}
      footer={
        matched && onTrade ? (
          <button
            type="button"
            onClick={onTrade}
            className="w-full rounded-lg bg-accent py-3 text-sm font-semibold text-white"
          >
            Trade {item?.primaryTicker}
          </button>
        ) : null
      }
    >
      <div className="space-y-4 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-sky-300">
          {item?.sourceName || item?.source}
        </p>
        <h2 className="text-lg font-bold text-text-primary">{item?.title}</h2>
        {matched && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-text-secondary">Alert price</p>
              <p className="text-sm text-text-primary">
                {formatPrice(item?.alertPrice)}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-secondary">Current price</p>
              <p className="text-sm text-text-primary">{formatPrice(livePrice)}</p>
            </div>
          </div>
        )}
        <p className="text-sm leading-relaxed text-text-secondary whitespace-pre-wrap">
          {item?.body}
        </p>
        {item?.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className="inline-block text-sm text-sky-300 underline"
          >
            Open article
          </a>
        )}
      </div>
    </DesktopSheet>
  );
};
